import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useLocation, useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, Grid, IconButton, Stack, TextField, Typography } from '@mui/material';
import get from 'lodash/get';

import { WhiteHeaderMUIDataGrid } from 'components/ThemedMUIDataGrid/WhiteHeaderMUIDataGrid';

import PatientReimbursementApi from '../../../../../../api/patient/PatientReimbursementApi';
import Section from '../../../../../../common/data-display/Section/Section';
import ModalBoxes from '../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import NotificationManager from '../../../../../../common/notifications/NotificationManager';
import { STDT_REIMBURSEMENT } from '../../../../../../constants/study/studyTypes';
import { toBill } from '../../../../../../services/financial';
import { onRequestError } from '../../../../../../services/handlers';
import { muiDataGridLocaleText } from '../../../../../../services/i18n/mui';
import { NS_COMMON, NSC_PATIENT, NSPP_PATIENT, NSPP_REIMBURSEMENT } from '../../../../../../services/i18n/namespaces';
import { getPatientAgeFromDob, getPatientPreferredName } from '../../../../../../services/patient';
import { PageInfoHeader } from '../../../../../PageInfoHeader/PageInfoHeader';
import { useCurrentRoute } from '../../../../../root/router';
import { PatientInfoHeaderContext, PatientInfoHeaderProvider } from '../../PatientInfoHeaderContext';
import {
  INVALID_PID,
  RBS_APPROVED,
  RBS_CANCELED,
  RBS_DENIED,
  RBS_DRAFT,
  RBS_PENDING
} from '../EncountersSection/PaymentsSection/reimbursementConstants';
import { TAB_PAYMENTS } from '../EncountersSection/useActiveTab';

import AddOrEditPaymentsItemsModal from './AddOrEditPaymentsItemsModal/AddOrEditPaymentsItemsModal';
import { prepareResponse } from './AddOrEditPaymentsItemsModal/services';
import StatusDropdown from './StatusDropdown/StatusDropdown';
import useGridData from './useGridData';

import './ReimbursementRequest.scss';

function ReimbursementRequestComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { ssuPatientId, reimbursementId, patientId } = useParams();
  const currentRoute = useCurrentRoute();
  const { patientInfo } = useContext(PatientInfoHeaderContext);
  const [loading, setLoading] = useState(true);
  const [reimbursement, setReimbursement] = useState(null);
  const { canBeApprovedOrDenied, status } = reimbursement || {};
  const [invalidCard, setInvalidCard] = useState(false);
  const [comment, setComment] = useState();
  const commentDisabled = reimbursement?.status === RBS_APPROVED;
  const commentSaveDisabled =
    commentDisabled || (!!reimbursement?.comment ? comment === reimbursement?.comment : !!!comment);
  const { submitAllowed, addItemAllowed, editItemAllowed, approveOrDeniedAllowed, isFromWorklist } = useMemo(
    function() {
      const submitAllowed = status === RBS_DRAFT;
      const approveOrDeniedAllowed = status === RBS_PENDING && canBeApprovedOrDenied;
      const isFromWorklist = currentRoute.key === 'Reimbursements.Reimbursement request';
      const addItemAllowed = ![RBS_APPROVED, RBS_CANCELED].includes(status);
      const editItemAllowed = ![RBS_APPROVED, RBS_CANCELED].includes(status);
      return { submitAllowed, addItemAllowed, editItemAllowed, approveOrDeniedAllowed, isFromWorklist };
    },
    [canBeApprovedOrDenied, currentRoute.key, status]
  );

  const [columns, gridData, setGridData] = useGridData(ssuPatientId, reimbursementId, editItemAllowed);

  const totalAmount = gridData.reduce((a, { amount }) => a + amount, 0);

  const navigateBack = useCallback(
    function() {
      const segments = location.pathname.split('/').filter(Boolean);
      const path = '/' + segments.slice(0, isFromWorklist ? 1 : 3).join('/') || '';
      navigate(
        isFromWorklist
          ? { pathname: path }
          : {
              pathname: path,
              search: createSearchParams({
                encounterTab: TAB_PAYMENTS,
                paymentType: STDT_REIMBURSEMENT
              }).toString()
            },
        { relative: 'path' }
      );
    },
    [isFromWorklist, navigate, location]
  );

  const handleNavigateBack = useCallback(
    function() {
      if (!commentSaveDisabled) {
        ModalBoxes.confirm({
          title: t(`${NSPP_REIMBURSEMENT}:request.discardReimbursementRequestCommentChanges`),
          content: t(`${NSPP_REIMBURSEMENT}:request.areYouSureToDiscard`),
          cancelButton: t('no'),
          confirmButton: t('yes')
        }).then(
          () => navigateBack(),
          () => {}
        );
      } else {
        navigateBack();
      }
    },
    [commentSaveDisabled, navigateBack, t]
  );

  const loadData = useCallback(
    function() {
      setLoading(true);
      PatientReimbursementApi.getReimbursement(ssuPatientId, reimbursementId).then(function({ data }) {
        const { items, ...rest } = data;
        setGridData(items);
        setReimbursement(rest);
        setComment(rest.comment);
        setLoading(false);
      });
    },
    [reimbursementId, setGridData, ssuPatientId]
  );

  useEffect(
    function() {
      loadData();
    },
    [loadData]
  );

  return (
    <>
      <PageInfoHeader
        objectRecordLabel={patientInfo?.name + getPatientPreferredName(patientInfo)}
        pageInfo={
          <PageInfoHeader.CollapsibleList>
            <PageInfoHeader.AdditionalInfo title={t(`${NSC_PATIENT}:patientId`)}>
              {patientInfo?.patientId}
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo title={t(`${NSC_PATIENT}:subjectId`)}>
              {patientInfo?.subjectId || t(`${NSPP_PATIENT}:info.noSubjectId`)}
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip={t(`${NS_COMMON}:dateOfBirth`)}>
              {patientInfo?.dob} ({getPatientAgeFromDob(patientInfo)})
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip={t(`${NS_COMMON}:study`)}>
              {patientInfo?.studyName}
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip={t(`${NS_COMMON}:site`)}>
              {patientInfo?.siteName}
            </PageInfoHeader.AdditionalInfo>
          </PageInfoHeader.CollapsibleList>
        }
      />
      <div className="eds-reimbursement-request">
        <Section>
          <Box sx={{ flexGrow: 1, pb: 5 }}>
            <Grid
              container
              spacing={3}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Grid>
                <Grid
                  container
                  spacing={3}
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Grid>
                    <Typography variant="h6">{reimbursement?.encounterName}</Typography>
                  </Grid>
                  <Grid>
                    <StatusDropdown
                      changeStatus={changeStatus}
                      totalAmount={totalAmount}
                      status={reimbursement?.status}
                      canBeApprovedOrDenied={reimbursement?.canBeApprovedOrDenied}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid>
                <IconButton
                  onClick={function() {
                    handleNavigateBack();
                  }}
                >
                  <CloseIcon data-testid="CloseIcon" />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Grid
              container
              spacing={3}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Grid>
                <Typography variant="h5" sx={{ color: '#691E44' }}>
                  {t(`${NSPP_REIMBURSEMENT}:request.reimbursementItemsList`)}
                </Typography>
                {invalidCard && (
                  <Typography variant="body1" color="error">
                    {t(`${NSPP_REIMBURSEMENT}:request.validPidNotFound`)}
                  </Typography>
                )}
              </Grid>
              <Grid>
                <Button
                  disabled={!addItemAllowed}
                  variant="outlined"
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={function() {
                    ModalBoxes.open({
                      component: (
                        <AddOrEditPaymentsItemsModal
                          ssuPatientInfo={getSsuPatientInfo(patientInfo)}
                          reimbursement={reimbursement}
                          reimbursementId={reimbursementId}
                          patientId={patientId}
                          ssuPatientId={ssuPatientId}
                          studyId={patientInfo?.studyId}
                          studyType={patientInfo?.studyType}
                          ssuId={patientInfo?.ssuId}
                        />
                      ),
                      onClose() {
                        loadData();
                      }
                    });
                  }}
                >
                  {t(`${NSPP_REIMBURSEMENT}:request.item`)}
                </Button>
              </Grid>
            </Grid>
          </Box>
          <WhiteHeaderMUIDataGrid
            loading={loading}
            rowHeight={38}
            rows={gridData}
            columns={columns}
            disableRowSelectionOnClick
            localeText={muiDataGridLocaleText({ noRowsLabel: t(`noResultsToDisplay`) })}
            onRowClick={function({ row }) {
              const { files, ...rest } = row;
              ModalBoxes.open({
                component: (
                  <AddOrEditPaymentsItemsModal
                    ssuPatientInfo={getSsuPatientInfo(patientInfo)}
                    editMode
                    viewMode={!editItemAllowed}
                    reimbursement={reimbursement}
                    reimbursementId={reimbursementId}
                    patientId={patientId}
                    ssuPatientId={ssuPatientId}
                    studyId={patientInfo?.studyId}
                    studyType={patientInfo?.studyType}
                    ssuId={patientInfo?.ssuId}
                    initialValues={prepareResponse({ ...rest })}
                    initialFiles={files}
                    fileUrlGetter={function({ id, itemId }) {
                      return PatientReimbursementApi.getReimbursementFile(
                        ssuPatientId,
                        reimbursementId,
                        itemId,
                        id
                      ).then(function({ data }) {
                        return `data:${data.fileType};base64,${data.content}`;
                      });
                    }}
                  />
                ),
                onClose() {
                  loadData();
                }
              });
            }}
            disableRowGrouping
            disableAggregation
            disablePivoting
          />
          <div className="reimbursement-request-comment-field">
            <Stack
              spacing={3}
              direction="row"
              sx={{
                justifyContent: 'space-between'
              }}
            >
              <label>{t('comment')}</label>
              <Button
                startIcon={<SaveIcon />}
                size="small"
                onClick={() => saveCommentHandler(false)}
                disabled={commentSaveDisabled}
              >
                {t('save')}
              </Button>
            </Stack>
            <TextField
              name="COVAL"
              disabled={commentDisabled}
              value={comment}
              onChange={e => {
                setComment(e.target.value);
              }}
              fullWidth
              multiline
              maxRows={3}
              minRows={2}
              variant="outlined"
              slotProps={{
                htmlInput: { maxLength: 500 }
              }}
            />
          </div>
          <Stack
            spacing={3}
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'end',
              py: 3
            }}
          >
            <Typography variant="body1">
              {t(`${NSPP_REIMBURSEMENT}:request.total`, { value: toBill(totalAmount) })}
            </Typography>
            {!isFromWorklist && (
              <Button
                variant="contained"
                disabled={!submitAllowed || invalidCard}
                onClick={function() {
                  changeStatus(RBS_PENDING);
                }}
              >
                {t(`${NSPP_REIMBURSEMENT}:request.submitRequest`)}
              </Button>
            )}
            {isFromWorklist && (
              <Stack spacing={2} direction="row">
                <Button
                  variant="contained"
                  disabled={!approveOrDeniedAllowed || totalAmount === 0}
                  color="error"
                  onClick={function() {
                    changeStatus(RBS_DENIED);
                  }}
                >
                  {t('deny')}
                </Button>
                <Button
                  color="success"
                  variant="contained"
                  disabled={!approveOrDeniedAllowed || invalidCard || totalAmount === 0}
                  onClick={function() {
                    changeStatus(RBS_APPROVED);
                  }}
                >
                  {t('approve')}
                </Button>
              </Stack>
            )}
          </Stack>
        </Section>
      </div>
    </>
  );

  function changeStatus(status) {
    (async function() {
      try {
        if (!commentSaveDisabled) {
          await saveCommentHandler(true);
        }
        if (status === RBS_CANCELED) {
          await ModalBoxes.confirm({
            content: 'Are you sure you want to cancel the reimbursement request?'
          });
        }

        PatientReimbursementApi.updateReimbursementStatus(ssuPatientId, reimbursementId, status, totalAmount)
          .then(() => {
            setInvalidCard(false);
            setReimbursement(state => ({ ...state, status }));
            NotificationManager.success(statusChangeNotification(status));
            if (status !== RBS_DRAFT) {
              navigateBack();
            }
          })
          .catch(error => {
            const { messageLocation = 'response.data.message', customMessage = null } = error.params || {};
            const message = customMessage || get(error, messageLocation, null);
            if (message === INVALID_PID) {
              setInvalidCard(true);
            } else {
              onRequestError(error, error.params);
            }
          });
      } catch (e) {
        if (e) {
          onRequestError(e);
        }
      }
    })();
  }

  async function saveCommentHandler(autosave) {
    await PatientReimbursementApi.addComment(ssuPatientId, reimbursementId, comment);
    NotificationManager.success(autosave ? 'Comment was saved' : 'Comment was added');
    setReimbursement(state => ({ ...state, comment }));
  }
}

function getSsuPatientInfo(patientInfo) {
  return {
    patientFullName: patientInfo?.name,
    patientId: patientInfo?.patientId,
    subjectId: patientInfo.subjectId,
    dateOfBirth: patientInfo?.dob,
    studyName: patientInfo.studyName,
    siteName: patientInfo.siteName
  };
}

export function statusChangeNotification(status) {
  if (status === RBS_PENDING) {
    return 'Reimbursement Request was submitted successfully';
  }
  if (status === RBS_CANCELED) {
    return 'Reimbursement Request was canceled';
  }
  if (status === RBS_APPROVED) {
    return 'Reimbursement Request was approved successfully';
  }
  if (status === RBS_DENIED) {
    return 'Request status was updated to denied';
  }
  return 'Reimbursement Request was changed successfully';
}

export default function ReimbursementRequest() {
  return (
    <PatientInfoHeaderProvider>
      <ReimbursementRequestComponent />
    </PatientInfoHeaderProvider>
  );
}
